import { getPathLocale } from "../utils/ogPathLocale";

export interface LanguageOption {
  code: string;
  label: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  // Tier 1: Primary International
  { code: 'en', label: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'zh', label: 'Chinese (Simplified)', nativeName: '中文 (简体)', flag: '🇨🇳' },
  { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'fr', label: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'ar', label: 'Arabic', nativeName: 'العربية', flag: '🇦🇪' },
  { code: 'de', label: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },

  // Tier 2 / 3: Regional & Community
  { code: 'ru', label: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'id', label: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'it', label: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'bn', label: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩' },
];

export const SUPPORTED_LANGUAGE_CODES: string[] = SUPPORTED_LANGUAGES.map((l) => l.code);

/**
 * Detect language from browser (navigator.languages or navigator.language).
 * Returns matching 2-letter language code if supported, otherwise null.
 */
export function getBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;

  const browserLanguages: readonly string[] =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : navigator.language
      ? [navigator.language]
      : [];

  for (const lang of browserLanguages) {
    if (!lang) continue;
    const primaryCode = lang.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGUAGE_CODES.includes(primaryCode)) {
      return primaryCode;
    }
  }

  return null;
}

/**
 * Determine initial language based on priority:
 * 0. OG path prefix /ru (link preview landing)
 * 1. Saved preference in localStorage
 * 2. Telegram WebApp user language_code
 * 3. Browser language
 * 4. Fallback to 'en'
 */
export function getInitialLanguage(telegramLanguageCode?: string): string {
  const pathLang = getPathLocale();
  if (pathLang && SUPPORTED_LANGUAGE_CODES.includes(pathLang)) {
    return pathLang;
  }

  try {
    const saved = localStorage.getItem('language');
    if (saved && SUPPORTED_LANGUAGE_CODES.includes(saved.toLowerCase())) {
      return saved.toLowerCase();
    }
  } catch (e) {
    // Ignore localStorage access errors
  }

  if (telegramLanguageCode) {
    const tgCode = telegramLanguageCode.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGUAGE_CODES.includes(tgCode)) {
      return tgCode;
    }
  }

  const browserLang = getBrowserLanguage();
  if (browserLang) {
    return browserLang;
  }

  return 'en';
}
